import React, { useCallback, useEffect, useState } from "react";
import jsPDF from "jspdf";
import api from "../../services/api";
import tenantCustomerService from "../../services/tenantCustomerService";
import styles from "./Reports.module.css";
import { ButtonBack } from "../../components/ui";
import { useFetch } from "../../hooks/useFetch";
import DataTable from "../../components/DataTable/DataTable";
import Modal from "../../components/Modal/Modal";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatBRL = (value) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
    value ?? 0,
  );

// ─── Constantes ───────────────────────────────────────────────────────────────

// ⚠️ Mantido em sincronia com backend/src/routes/reportRoutes.ts (TIER_RANK).
// Ao alterar a escada, mude nos dois.
const TIER_RANK = { NONE: 0, SIMPLE: 1, COMPLETE: 2, ADVANCED: 3 };
const tierAtLeast = (userTier, required) =>
  (TIER_RANK[userTier] ?? 0) >= (TIER_RANK[required] ?? 0);

// Mapa tier de relatório → plano comercial mínimo que o libera. SIMPLE é
// visível já no FREE (nunca gera gate), por isso não aparece aqui: o gate só
// surge para abas COMPLETE/ADVANCED.
const TIER_TO_PLAN = { COMPLETE: "PRATA", ADVANCED: "OURO" };

// Rótulo amigável do tier de relatório (para linha secundária do badge).
const TIER_LABELS = { SIMPLE: "Simples", COMPLETE: "Completo", ADVANCED: "Avançado" };

const PERIOD_LABELS = {
  week: "Últimos 7 dias",
  month: "Último mês",
  quarter: "Último trimestre",
  year: "Último ano",
};

const REPORT_TYPES = [
  {
    key: "overview",
    label: "Visão Geral",
    icon: "📊",
    required: "SIMPLE",
    endpoint: "/reports/overview",
  },
  {
    key: "appointments",
    label: "Agendamentos",
    icon: "📅",
    required: "SIMPLE",
    endpoint: "/reports/appointments",
  },
  {
    key: "messages",
    label: "Mensagens",
    icon: "💬",
    required: "SIMPLE",
    endpoint: "/reports/messages",
  },
  {
    key: "clients",
    label: "Clientes",
    icon: "👥",
    required: "COMPLETE",
    endpoint: "/reports/clients",
  },
  {
    key: "revenue",
    label: "Receita",
    icon: "💰",
    required: "COMPLETE",
    endpoint: "/reports/revenue",
  },
  {
    key: "gaps",
    label: "Horários Livres",
    icon: "🕐",
    required: "COMPLETE",
    endpoint: "/reports/gaps",
  },
  {
    key: "insights",
    label: "Insights IA",
    icon: "🤖",
    required: "ADVANCED",
    endpoint: "/reports/insights",
  },
];

// ─── Helpers de export ────────────────────────────────────────────────────────

function downloadCSV(rows, filename) {
  const csv = rows
    .map((r) =>
      r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(","),
    )
    .join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function buildCSVRows(reportKey, data) {
  if (!data) return [["Sem dados"]];
  switch (reportKey) {
    case "overview":
      return [
        ["Métrica", "Valor"],
        ["Total de Agendamentos", data.overview?.totalAppointments],
        ["Confirmados", data.overview?.confirmedAppointments],
        ["Cancelados", data.overview?.cancelledAppointments],
        ["Mensagens Enviadas", data.overview?.totalMessages],
        ["Sessões Ativas", data.overview?.activeSessions],
      ];
    case "appointments":
      return [
        ["Data", "Confirmados", "Cancelados"],
        ...(data.dailyData ?? []).map((d) => [
          d.date,
          d.confirmed,
          d.cancelled,
        ]),
        [],
        ["Serviço", "Agendamentos", "Receita"],
        ...(data.byService ?? []).map((s) => [
          s.name,
          s.count,
          formatBRL(s.revenue),
        ]),
        ...(data.topClients
          ? [
              [],
              ["Cliente", "Telefone", "Agendamentos"],
              ...data.topClients.map((c) => [c.name, c.phone, c.count]),
            ]
          : []),
      ];
    case "messages":
      return [
        ["Data", "Mensagens"],
        ...(data.dailyData ?? []).map((d) => [d.date, d.count]),
        [],
        ["Plataforma", "Mensagens"],
        ...Object.entries(data.summary?.byPlatform ?? {}).map(([p, c]) => [
          p,
          c,
        ]),
      ];
    case "clients":
      return [
        [
          "Nome",
          "Telefone",
          "E-mail",
          "Agendamentos",
          "Cancelamentos",
          "Serviços",
          "Última visita",
          "Receita",
        ],
        ...(data.clients ?? []).map((c) => [
          c.name,
          c.phone,
          c.email ?? "",
          c.appointments,
          c.cancelled,
          c.services,
          c.lastSeen,
          formatBRL(c.totalRevenue),
        ]),
      ];
    case "revenue":
      return [
        ["Métrica", "Valor"],
        ["Receita Total", formatBRL(data.summary?.totalRevenue)],
        ["Agendamentos Confirmados", data.summary?.totalAppointments],
        ["Ticket Médio", formatBRL(data.summary?.avgTicket)],
        ...(data.projectedMonthly != null ? [["Projeção Mensal", formatBRL(data.projectedMonthly)]] : []),
        [],
        ["Serviço", "Agendamentos", "Receita"],
        ...(data.byService ?? []).map((s) => [s.name, s.count, formatBRL(s.revenue)]),
        [],
        ["Data", "Receita do Dia"],
        ...(data.dailyRevenue ?? []).map((d) => [d.date, formatBRL(d.revenue)]),
      ];
    case "gaps":
      return [
        ["Hora", "Agendamentos", "Gap?"],
        ...(data.hourData ?? []).map((h) => [h.hour, h.count, h.isGap ? "Sim" : "Não"]),
        [],
        ["Dia da Semana", "Agendamentos", "Gap?"],
        ...(data.dowData ?? []).map((d) => [d.day, d.count, d.isGap ? "Sim" : "Não"]),
      ];
    case "insights":
      return [
        ["Tipo", "Conteúdo"],
        ["Principal Oportunidade", data.topOpportunity ?? ""],
        ...(data.insights ?? []).map((ins, i) => [`Insight ${i + 1}`, ins]),
      ];
    default:
      return [["Sem dados"]];
  }
}

function generatePDF(reportKey, data, tenantName, period) {
  const doc = new jsPDF();
  const PAGE_W = doc.internal.pageSize.getWidth();
  const MARGIN = 14;

  // Cabeçalho
  doc.setFillColor(110, 10, 212);
  doc.rect(0, 0, PAGE_W, 28, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text("Nexus Tech — Relatórios", MARGIN, 12);
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text(tenantName, MARGIN, 21);

  // Info
  doc.setTextColor(80, 80, 80);
  doc.setFontSize(9);
  const periodLabel = PERIOD_LABELS[period] ?? period;
  const fromDate = data.dateRange?.from?.split("T")[0] ?? "";
  const toDate = data.dateRange?.to?.split("T")[0] ?? "";
  const rangeStr = fromDate ? `${fromDate} a ${toDate}` : periodLabel;
  doc.text(`Período: ${rangeStr}`, MARGIN, 35);
  doc.text(
    `Gerado em: ${new Date().toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}`,
    PAGE_W - MARGIN,
    35,
    { align: "right" },
  );
  doc.setDrawColor(220, 220, 220);
  doc.line(MARGIN, 39, PAGE_W - MARGIN, 39);

  let y = 47;

  const drawSectionTitle = (title) => {
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(110, 10, 212);
    doc.text(title, MARGIN, y);
    y += 7;
    doc.setFont("helvetica", "normal");
    doc.setTextColor(40, 40, 40);
  };

  const drawTable = (headers, rows) => {
    const colW = (PAGE_W - MARGIN * 2) / headers.length;
    // Header row
    doc.setFillColor(245, 240, 255);
    doc.rect(MARGIN, y - 4, PAGE_W - MARGIN * 2, 8, "F");
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(80, 40, 140);
    headers.forEach((h, i) => doc.text(String(h), MARGIN + i * colW + 2, y));
    y += 8;
    doc.setFont("helvetica", "normal");
    doc.setTextColor(40, 40, 40);
    rows.forEach((row, ri) => {
      if (y > 265) {
        doc.addPage();
        y = 20;
      }
      if (ri % 2 === 0) {
        doc.setFillColor(250, 250, 252);
        doc.rect(MARGIN, y - 4, PAGE_W - MARGIN * 2, 7, "F");
      }
      doc.setFontSize(9);
      row.forEach((cell, i) =>
        doc.text(String(cell ?? "").substring(0, 30), MARGIN + i * colW + 2, y),
      );
      y += 7;
    });
    y += 5;
  };

  const drawMetricCards = (items) => {
    const cardW = (PAGE_W - MARGIN * 2) / items.length;
    items.forEach((item, i) => {
      doc.setFillColor(248, 246, 255);
      doc.roundedRect(MARGIN + i * cardW, y, cardW - 4, 18, 2, 2, "F");
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(110, 10, 212);
      doc.text(String(item.value), MARGIN + i * cardW + cardW / 2 - 4, y + 10);
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(100, 100, 100);
      doc.text(item.label, MARGIN + i * cardW + cardW / 2 - 4, y + 16, {
        maxWidth: cardW - 6,
      });
    });
    y += 26;
  };

  // Conteúdo por tipo
  if (reportKey === "overview" && data.overview) {
    drawSectionTitle("Resumo do Mês Atual");
    drawMetricCards([
      { label: "Agendamentos", value: data.overview.totalAppointments },
      { label: "Confirmados", value: data.overview.confirmedAppointments },
      { label: "Cancelados", value: data.overview.cancelledAppointments },
      { label: "Mensagens", value: data.overview.totalMessages },
      { label: "Sessões", value: data.overview.activeSessions },
    ]);
  }

  if (reportKey === "appointments") {
    if (data.summary) {
      drawSectionTitle("Resumo de Agendamentos");
      drawMetricCards([
        { label: "Total", value: data.summary.total },
        { label: "Confirmados", value: data.summary.confirmed },
        { label: "Cancelados", value: data.summary.cancelled },
        { label: "Taxa Cancelam.", value: data.summary.cancellationRate },
      ]);
    }
    if (data.byService?.length) {
      drawSectionTitle("Desempenho por Serviço");
      drawTable(
        ["Serviço", "Agend.", "Receita (R$)"],
        data.byService.map((s) => [s.name, s.count, s.revenue?.toFixed(2)]),
      );
    }
    if (data.dailyData?.length) {
      drawSectionTitle("Histórico Diário");
      drawTable(
        ["Data", "Confirmados", "Cancelados"],
        data.dailyData.map((d) => [d.date, d.confirmed, d.cancelled]),
      );
    }
    if (data.topClients?.length) {
      drawSectionTitle("Top Clientes");
      drawTable(
        ["Nome", "Telefone", "Agendamentos"],
        data.topClients.map((c) => [c.name, c.phone, c.count]),
      );
    }
    if (data.peakHours?.length) {
      drawSectionTitle("Horários de Pico");
      drawTable(
        ["Horário", "Agendamentos"],
        data.peakHours.map((h) => [h.hour, h.count]),
      );
    }
  }

  if (reportKey === "messages") {
    if (data.summary) {
      drawSectionTitle("Resumo de Mensagens");
      drawMetricCards([
        { label: "Mensagens", value: data.summary.totalMessages },
        { label: "Sessões", value: data.summary.totalSessions },
        { label: "WhatsApp", value: data.summary.whatsappSessions },
        { label: "Instagram", value: data.summary.instagramSessions },
      ]);
    }
    if (data.engagement) {
      doc.setFontSize(9);
      doc.setTextColor(60, 60, 60);
      doc.text(
        `Média de msgs/sessão: ${data.engagement.avgMessagesPerSession}   |   Atendimentos humanos: ${data.engagement.humanHandoffs}`,
        MARGIN,
        y,
      );
      y += 10;
    }
    if (data.dailyData?.length) {
      drawSectionTitle("Mensagens por Dia");
      drawTable(
        ["Data", "Mensagens"],
        data.dailyData.map((d) => [d.date, d.count]),
      );
    }
    if (data.peakHours?.length) {
      drawSectionTitle("Horários de Pico");
      drawTable(
        ["Horário", "Mensagens"],
        data.peakHours.map((h) => [h.hour, h.count]),
      );
    }
  }

  if (reportKey === "clients") {
    if (data.summary) {
      drawSectionTitle("Resumo de Clientes");
      drawMetricCards([
        { label: "Clientes únicos", value: data.summary.uniqueClients },
        { label: "Novos", value: data.summary.newClients },
        { label: "Recorrentes", value: data.summary.returningClients },
        { label: "Sessões totais", value: data.summary.totalSessions },
      ]);
    }
    if (data.clients?.length) {
      drawSectionTitle("Lista de Clientes");
      drawTable(
        ["Nome", "Telefone", "Agend.", "Canc.", "Última visita"],
        data.clients.map((c) => [
          c.name,
          c.phone,
          c.appointments,
          c.cancelled,
          c.lastSeen,
        ]),
      );
    }
  }

  // Rodapé
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(160, 160, 160);
    doc.text(
      `Página ${i} de ${pageCount}  —  Nexus Tech Relatórios`,
      PAGE_W / 2,
      290,
      { align: "center" },
    );
  }

  doc.save(
    `relatorio-${reportKey}-${new Date().toISOString().split("T")[0]}.pdf`,
  );
}

// ─── Componentes auxiliares ───────────────────────────────────────────────────

function MetricCard({ label, value, sub, color = "#6e0ad4", wide = false }) {
  return (
    <div
      className={styles.metricCard}
      style={wide ? { gridColumn: "span 2", minWidth: 0 } : undefined}
    >
      <div className={styles.metricValue} style={{ color }}>
        {value ?? "—"}
      </div>
      <div className={styles.metricLabel}>{label}</div>
      {sub && <div className={styles.metricSub}>{sub}</div>}
    </div>
  );
}

export function UpgradePrompt({ requiredTier }) {
  // Nunca exibir o tier cru (SIMPLE/COMPLETE/ADVANCED não são nomes de plano).
  // Mapear para o plano comercial mínimo; sem mapeamento → copy genérica.
  const plan = TIER_TO_PLAN[requiredTier];
  return (
    <div className={styles.upgradePrompt}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>🔒</div>
      <h3>Recurso disponível em planos superiores</h3>
      <p>
        {plan ? (
          <>
            Disponível a partir do plano <strong>{plan}</strong>. Faça upgrade
            para acessar.
          </>
        ) : (
          "Faça upgrade do seu plano para acessar."
        )}
      </p>
    </div>
  );
}

// ─── Componente principal ──────────────────────────────────────────────────────

export default function ReportsPage() {
  const [activeReport, setActiveReport] = useState("overview");
  const [period, setPeriod] = useState("month");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tenantName, setTenantName] = useState("");
  const [userTier, setUserTier] = useState("NONE");
  const [planName, setPlanName] = useState("");

  // Carrega o plano do tenant uma vez
  const { data: tenantData } = useFetch("/tenants/me");

  useEffect(() => {
    if (!tenantData) return;
    setTenantName(tenantData?.tenant?.name ?? "");
    const tier = tenantData?.plan?.reportsType?.toUpperCase() ?? "NONE";
    setUserTier(tier);
    // Nome comercial do plano (ex.: "OURO ANUAL"), não o tier cru de relatório.
    setPlanName(tenantData?.plan?.name ?? "");
  }, [tenantData]);

  const fetchReport = useCallback(async () => {
    const reportDef = REPORT_TYPES.find((r) => r.key === activeReport);
    if (!reportDef) return;
    if (!tierAtLeast(userTier, reportDef.required)) {
      setData(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ period });
      if (period === "custom" && dateFrom && dateTo) {
        params.set("dateFrom", dateFrom);
        params.set("dateTo", dateTo);
      }
      const res = await api.get(`${reportDef.endpoint}?${params}`);
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.error ?? "Erro ao carregar relatório");
    } finally {
      setLoading(false);
    }
  }, [activeReport, period, dateFrom, dateTo, userTier]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleExportPDF = () => {
    if (data) generatePDF(activeReport, data, tenantName, period);
  };

  const handleExportCSV = () => {
    if (data)
      downloadCSV(
        buildCSVRows(activeReport, data),
        `relatorio-${activeReport}-${new Date().toISOString().split("T")[0]}.csv`,
      );
  };

  const activeReportDef = REPORT_TYPES.find((r) => r.key === activeReport);
  const canView = tierAtLeast(userTier, activeReportDef?.required ?? "SIMPLE");

  return (
    <div className={styles.page}>
      {/* Sub-navegação da tela de Relatórios (tipos de relatório) como abas no
          topo. Antes era um trilho vertical próprio (2º trilho à esquerda); agora
          é horizontal, deixando o único trilho lateral para a Sidebar global do
          AppLayout. */}
      <header className={styles.tabsHeader}>
        <div className={styles.tabsHeaderTop}>
          <ButtonBack to="/agenda" />
          <span className={styles.tierBadge}>
            Plano atual: <strong>{planName || "—"}</strong>
            {TIER_LABELS[userTier] && (
              <span className={styles.tierBadgeSub}>
                {" "}
                · Relatórios: {TIER_LABELS[userTier]}
              </span>
            )}
          </span>
        </div>
        <nav
          className={styles.tabs}
          role="tablist"
          aria-label="Tipos de relatório"
        >
          {REPORT_TYPES.map((r) => {
            const locked = !tierAtLeast(userTier, r.required);
            return (
              <button
                key={r.key}
                role="tab"
                aria-selected={activeReport === r.key}
                onClick={() => {
                  if (!locked) {
                    setActiveReport(r.key);
                  }
                }}
                className={`${styles.tab} ${activeReport === r.key ? styles.tabActive : ""} ${locked ? styles.tabLocked : ""}`}
                title={locked ? `Requer plano ${r.required}` : r.label}
                aria-disabled={locked ? "true" : undefined}
              >
                <span>{r.icon}</span>
                <span>{r.label}</span>
                {locked && <span className={styles.lockIcon}>🔒</span>}
              </button>
            );
          })}
        </nav>
      </header>

      {/* Conteúdo principal */}
      <div className={styles.main}>
        {/* Toolbar */}
        <div className={styles.toolbar}>
          <div className={styles.toolbarLeft}>
            <h1 className={styles.reportTitle}>
              {activeReportDef?.icon} {activeReportDef?.label}
            </h1>
          </div>
          <div className={styles.toolbarRight}>
            {tierAtLeast(userTier, "ADVANCED") ? (
              <div className={styles.dateRangePicker}>
                <select
                  value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                  className={styles.periodSelect}
                >
                  {Object.entries(PERIOD_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                  <option value="custom">Período personalizado</option>
                </select>
                {period === "custom" && (
                  <>
                    <input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      className={styles.dateInput}
                    />
                    <span>até</span>
                    <input
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      className={styles.dateInput}
                    />
                  </>
                )}
              </div>
            ) : (
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className={styles.periodSelect}
              >
                {Object.entries(PERIOD_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            )}
            {canView && data && (
              <div className={styles.exportBtns}>
                <button onClick={handleExportPDF} className={styles.btnPDF}>
                  ⬇ PDF
                </button>
                <button onClick={handleExportCSV} className={styles.btnCSV}>
                  ⬇ CSV
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Corpo do relatório */}
        <div className={styles.content}>
          {!canView && (
            <UpgradePrompt requiredTier={activeReportDef?.required} />
          )}

          {canView && loading && (
            <div className={styles.loadingState}>
              <div className={styles.spinner} />
              <p>Carregando relatório...</p>
            </div>
          )}

          {canView && error && !loading && (
            <div className={styles.errorState}>⚠️ {error}</div>
          )}

          {canView && !loading && !error && data && (
            <ReportContent
              reportKey={activeReport}
              data={data}
              userTier={userTier}
              onClientUpdated={fetchReport}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Renderizador de conteúdo ─────────────────────────────────────────────────

function ReportContent({ reportKey, data, userTier, onClientUpdated }) {
  switch (reportKey) {
    case "overview":
      return <OverviewReport data={data} />;
    case "appointments":
      return <AppointmentsReport data={data} userTier={userTier} />;
    case "messages":
      return <MessagesReport data={data} userTier={userTier} />;
    case "clients":
      return <ClientsReport data={data} onClientUpdated={onClientUpdated} />;
    case "revenue":
      return <RevenueReport data={data} userTier={userTier} />;
    case "gaps":
      return <GapsReport data={data} />;
    case "insights":
      return <InsightsReport data={data} />;
    default:
      return null;
  }
}

function OverviewReport({ data }) {
  const ov = data.overview ?? {};
  const cancelRate =
    ov.totalAppointments > 0
      ? ((ov.cancelledAppointments / ov.totalAppointments) * 100).toFixed(1)
      : "0";
  return (
    <section>
      <p className={styles.periodInfo}>Mês atual (1º até hoje)</p>
      <div className={styles.cardsGrid}>
        <MetricCard
          label="Total de Agendamentos"
          value={ov.totalAppointments}
          color="#6e0ad4"
        />
        <MetricCard
          label="Confirmados"
          value={ov.confirmedAppointments}
          color="#10b981"
        />
        <MetricCard
          label="Cancelados"
          value={ov.cancelledAppointments}
          color="#ef4444"
          sub={`${cancelRate}% de cancelamento`}
        />
        <MetricCard
          label="Mensagens Enviadas"
          value={ov.totalMessages}
          color="#3b82f6"
        />
        <MetricCard
          label="Conversas Ativas"
          value={ov.activeSessions}
          color="#f59e0b"
        />
      </div>
    </section>
  );
}

function AppointmentsReport({ data, userTier }) {
  const s = data.summary ?? {};
  return (
    <section>
      <div className={styles.cardsGrid}>
        <MetricCard label="Total" value={s.total} color="#6e0ad4" />
        <MetricCard label="Confirmados" value={s.confirmed} color="#10b981" />
        <MetricCard label="Cancelados" value={s.cancelled} color="#ef4444" />
        <MetricCard
          label="Taxa Cancelam."
          value={s.cancellationRate}
          color="#f59e0b"
        />
      </div>

      <h3 className={styles.sectionTitle}>Desempenho por Serviço</h3>
      <DataTable
        headers={["Serviço", "Agendamentos", "Receita"]}
        rows={data.byService?.map((s) => [s.name, s.count, formatBRL(s.revenue)])}
      />

      <h3 className={styles.sectionTitle}>Histórico Diário</h3>
      <DataTable
        headers={["Data", "Confirmados", "Cancelados"]}
        rows={data.dailyData?.map((d) => [d.date, d.confirmed, d.cancelled])}
      />

      {tierAtLeast(userTier, "COMPLETE") && data.topClients?.length > 0 && (
        <>
          <h3 className={styles.sectionTitle}>Top 10 Clientes</h3>
          <DataTable
            headers={["Nome", "Telefone", "Agendamentos"]}
            rows={data.topClients?.map((c) => [c.name, c.phone, c.count])}
          />
        </>
      )}

      {tierAtLeast(userTier, "ADVANCED") && data.peakHours?.length > 0 && (
        <>
          <h3 className={styles.sectionTitle}>Horários de Pico</h3>
          <PeakHoursBar data={data.peakHours} />
        </>
      )}
    </section>
  );
}

function MessagesReport({ data, userTier }) {
  const s = data.summary ?? {};
  return (
    <section>
      <div className={styles.cardsGrid}>
        <MetricCard
          label="Mensagens Enviadas"
          value={s.totalMessages}
          color="#6e0ad4"
        />
        <MetricCard
          label="Total de Sessões"
          value={s.totalSessions}
          color="#3b82f6"
        />
        <MetricCard
          label="WhatsApp"
          value={s.whatsappSessions}
          color="#25D366"
        />
        <MetricCard
          label="Instagram"
          value={s.instagramSessions}
          color="#E1306C"
        />
      </div>

      {tierAtLeast(userTier, "COMPLETE") && data.engagement && (
        <div className={styles.engagementRow}>
          <span>
            📬 Média de mensagens por sessão:{" "}
            <strong>{data.engagement.avgMessagesPerSession}</strong>
          </span>
          <span>
            👤 Atendimentos pelo agente humano:{" "}
            <strong>{data.engagement.humanHandoffs}</strong>
          </span>
          <span>
            📦 Total de clientes (histórico):{" "}
            <strong>{data.engagement.totalClientsAllTime}</strong>
          </span>
        </div>
      )}

      <h3 className={styles.sectionTitle}>Mensagens por Dia</h3>
      <DataTable
        headers={["Data", "Mensagens"]}
        rows={data.dailyData?.map((d) => [d.date, d.count])}
      />

      {tierAtLeast(userTier, "ADVANCED") && data.peakHours?.length > 0 && (
        <>
          <h3 className={styles.sectionTitle}>Horários de Pico</h3>
          <PeakHoursBar data={data.peakHours} />
        </>
      )}
    </section>
  );
}

function ClientsReport({ data, onClientUpdated }) {
  const s = data.summary ?? {};
  const [clients, setClients] = useState(data.clients ?? []);
  const [editingClient, setEditingClient] = useState(null);

  // Sync when data changes (period switch)
  useEffect(() => {
    setClients(data.clients ?? []);
  }, [data.clients]);

  const retentionRate =
    s.uniqueClients > 0
      ? ((s.returningClients / s.uniqueClients) * 100).toFixed(1)
      : "0";
  const totalRevenue = clients.reduce((sum, c) => sum + (c.totalRevenue ?? 0), 0);

  const handleSaved = (updated) => {
    setClients((prev) =>
      prev.map((c) => (c.id === updated.id ? { ...c, ...updated } : c)),
    );
    setEditingClient(null);
    onClientUpdated?.();
  };

  return (
    <section>
      <div className={styles.cardsGrid}>
        <MetricCard label="Clientes Únicos" value={s.uniqueClients} color="#6e0ad4" />
        <MetricCard label="Novos Clientes" value={s.newClients} color="#10b981" />
        <MetricCard
          label="Recorrentes"
          value={s.returningClients}
          color="#3b82f6"
          sub={`${retentionRate}% de retenção`}
        />
        <MetricCard label="Receita Total" value={formatBRL(totalRevenue)} color="#f59e0b" wide />
      </div>

      <h3 className={styles.sectionTitle}>Lista de Clientes</h3>
      {clients.length === 0 ? (
        <p className={styles.emptyMsg}>Sem clientes no período</p>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Nome</th>
                <th>Telefone</th>
                <th>E-mail</th>
                <th>Agend.</th>
                <th>Cancelados</th>
                <th>Serviços</th>
                <th>Última visita</th>
                <th>Receita</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => (
                <tr key={c.id}>
                  <td>{c.name}</td>
                  <td>{c.phone || "—"}</td>
                  <td>{c.email || "—"}</td>
                  <td>{c.appointments}</td>
                  <td>{c.cancelled}</td>
                  <td>{c.services || "—"}</td>
                  <td>{c.lastSeen || "—"}</td>
                  <td>{formatBRL(c.totalRevenue)}</td>
                  <td>
                    <button
                      onClick={() => setEditingClient(c)}
                      style={{
                        background: "none",
                        border: "1px solid #d1d5db",
                        borderRadius: 4,
                        padding: "2px 8px",
                        cursor: "pointer",
                        fontSize: "0.75rem",
                        color: "#6e0ad4",
                      }}
                    >
                      ✏️ Editar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editingClient && (
        <ClientEditModal
          client={editingClient}
          onClose={() => setEditingClient(null)}
          onSaved={handleSaved}
        />
      )}
    </section>
  );
}

function ClientEditModal({ client, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: client.name || "",
    email: client.email || "",
    notes: "",
    instagramHandle: "",
    birthday: "",
    telegramUsername: "",
  });
  const [loadingDetail, setLoadingDetail] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Carrega campos extras que o relatório não inclui (notes, birthday, etc.)
  useEffect(() => {
    tenantCustomerService.getCustomerDetail(client.id)
      .then((detail) => {
        setForm({
          name: detail.name || "",
          email: detail.email || "",
          notes: detail.notes || "",
          instagramHandle: detail.instagramHandle || "",
          birthday: detail.birthday
            ? new Date(detail.birthday).toISOString().split("T")[0]
            : "",
          telegramUsername: detail.telegramUsername || "",
        });
      })
      .catch(() => {})
      .finally(() => setLoadingDetail(false));
  }, [client.id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await tenantCustomerService.updateCustomer(client.id, {
        ...form,
        birthday: form.birthday || null,
      });
      onSaved({ id: client.id, name: form.name, email: form.email });
    } catch (err) {
      setError(err.response?.data?.error || "Erro ao salvar cliente.");
    } finally {
      setSaving(false);
    }
  };

  const fields = [
    { label: "Nome", name: "name", type: "text", placeholder: "Nome completo" },
    { label: "E-mail", name: "email", type: "email", placeholder: "email@exemplo.com" },
    { label: "Instagram", name: "instagramHandle", type: "text", placeholder: "@usuario" },
    { label: "Telegram", name: "telegramUsername", type: "text", placeholder: "@usuario" },
    { label: "Aniversário", name: "birthday", type: "date", placeholder: "" },
    { label: "Observações", name: "notes", type: "textarea", placeholder: "Anotações sobre o cliente..." },
  ];

  return (
    <Modal isOpen={true} onClose={onClose} width="440px" titleId="client-edit-modal-title">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem" }}>
        <h3 id="client-edit-modal-title" style={{ margin: 0, fontSize: "1.1rem", fontWeight: 600 }}>✏️ Editar Cliente</h3>
        <button onClick={onClose} style={{ background: "none", border: "none", fontSize: "1.25rem", cursor: "pointer", color: "#6b7280" }}>✕</button>
      </div>

      {error && (
        <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 6, padding: "0.5rem 0.75rem", marginBottom: "1rem", color: "#dc2626", fontSize: "0.875rem" }}>
          {error}
        </div>
      )}

      {loadingDetail ? (
        <div style={{ textAlign: "center", padding: "2rem", color: "#6b7280" }}>Carregando...</div>
      ) : (
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
          {fields.map(({ label, name, type, placeholder }) => (
            <div key={name}>
              <label style={{ display: "block", fontSize: "0.8rem", fontWeight: 500, color: "#374151", marginBottom: 4 }}>{label}</label>
              {type === "textarea" ? (
                <textarea
                  name={name}
                  value={form[name]}
                  onChange={handleChange}
                  placeholder={placeholder}
                  disabled={saving}
                  rows={3}
                  style={{ width: "100%", padding: "0.5rem 0.75rem", border: "1px solid #d1d5db", borderRadius: 6, fontSize: "0.9rem", boxSizing: "border-box", resize: "vertical" }}
                />
              ) : (
                <input
                  type={type}
                  name={name}
                  value={form[name]}
                  onChange={handleChange}
                  placeholder={placeholder}
                  disabled={saving}
                  style={{ width: "100%", padding: "0.5rem 0.75rem", border: "1px solid #d1d5db", borderRadius: 6, fontSize: "0.9rem", boxSizing: "border-box" }}
                />
              )}
            </div>
          ))}

          <div style={{ fontSize: "0.75rem", color: "#9ca3af", background: "#f9fafb", padding: "0.5rem 0.75rem", borderRadius: 6 }}>
            Telefone é gerenciado automaticamente pelo canal de contato (WhatsApp/Instagram).
          </div>

          <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem" }}>
            <button
              type="submit"
              disabled={saving}
              style={{ flex: 1, background: "#6e0ad4", color: "#fff", border: "none", borderRadius: 6, padding: "0.6rem", fontWeight: 600, cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.7 : 1 }}
            >
              {saving ? "Salvando..." : "💾 Salvar"}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              style={{ flex: 1, background: "#f3f4f6", color: "#374151", border: "none", borderRadius: 6, padding: "0.6rem", fontWeight: 500, cursor: "pointer" }}
            >
              Cancelar
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}

function PeakHoursBar({ data }) {
  const max = Math.max(...(data?.map((h) => h.count) ?? [1]), 1);
  return (
    <div className={styles.peakBars}>
      {data?.slice(0, 8).map((h) => (
        <div key={h.hour} className={styles.peakBarItem}>
          <div className={styles.peakBarTrack}>
            <div
              className={styles.peakBarFill}
              style={{ height: `${(h.count / max) * 100}%` }}
            />
          </div>
          <span className={styles.peakBarLabel}>{h.hour}</span>
          <span className={styles.peakBarCount}>{h.count}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Relatório de Receita ─────────────────────────────────────────────────────

function RevenueReport({ data, userTier }) {
  const s = data.summary ?? {};
  const dailyMax = Math.max(...(data.dailyRevenue ?? []).map((d) => d.revenue), 1);

  return (
    <section>
      <div className={styles.cardsGrid}>
        <MetricCard label="Receita Total" value={formatBRL(s.totalRevenue)} color="#6e0ad4" wide />
        <MetricCard label="Agendamentos" value={s.totalAppointments} color="#10b981" />
        <MetricCard label="Ticket Médio" value={formatBRL(s.avgTicket)} color="#3b82f6" />
        {data.projectedMonthly != null && (
          <MetricCard label="Projeção Mensal" value={formatBRL(data.projectedMonthly)} color="#f59e0b" />
        )}
      </div>

      {data.byService?.length > 0 && (
        <>
          <h3 className={styles.sectionTitle}>Receita por Serviço</h3>
          <DataTable
            headers={["Serviço", "Agendamentos", "Receita"]}
            rows={data.byService.map((s) => [s.name, s.count, formatBRL(s.revenue)])}
          />
        </>
      )}

      {data.dailyRevenue?.length > 0 && (
        <>
          <h3 className={styles.sectionTitle}>Receita Diária</h3>
          <div className={styles.peakBars} style={{ alignItems: "flex-end", height: 120 }}>
            {data.dailyRevenue.slice(-14).map((d) => (
              <div key={d.date} className={styles.peakBarItem}>
                <div className={styles.peakBarTrack}>
                  <div
                    className={styles.peakBarFill}
                    style={{ height: `${(d.revenue / dailyMax) * 100}%`, background: "#6e0ad4" }}
                  />
                </div>
                <span className={styles.peakBarLabel} style={{ fontSize: "0.6rem" }}>
                  {d.date.slice(5)}
                </span>
                <span className={styles.peakBarCount}>{formatBRL(d.revenue)}</span>
              </div>
            ))}
          </div>
        </>
      )}

      {tierAtLeast(userTier, "ADVANCED") && data.projectedMonthly != null && (
        <div className={styles.engagementRow} style={{ marginTop: "1.5rem" }}>
          <span>
            📈 Projeção para o mês completo (extrapolação linear):{" "}
            <strong>{formatBRL(data.projectedMonthly)}</strong>
          </span>
        </div>
      )}
    </section>
  );
}

// ─── Relatório de Horários com Baixa Demanda ──────────────────────────────────

function GapsReport({ data }) {
  const hourMax = Math.max(...(data.hourData ?? []).map((h) => h.count), 1);
  const dowMax = Math.max(...(data.dowData ?? []).map((d) => d.count), 1);

  return (
    <section>
      <div className={styles.cardsGrid}>
        <MetricCard
          label="Agendamentos analisados"
          value={data.totalAppointments}
          color="#6e0ad4"
        />
        <MetricCard
          label="Período"
          value="Últimos 60 dias"
          color="#3b82f6"
        />
      </div>

      {data.suggestions?.length > 0 && (
        <div style={{ margin: "1.5rem 0", padding: "1rem 1.25rem", background: "#fdf4ff", borderRadius: 8, borderLeft: "4px solid #6e0ad4" }}>
          <strong style={{ color: "#6e0ad4", display: "block", marginBottom: "0.5rem" }}>
            💡 Sugestões
          </strong>
          <ul style={{ margin: 0, paddingLeft: "1.25rem" }}>
            {data.suggestions.map((s, i) => (
              <li key={i} style={{ color: "#374151", marginBottom: "0.25rem", fontSize: "0.875rem" }}>{s}</li>
            ))}
          </ul>
        </div>
      )}

      <h3 className={styles.sectionTitle}>Demanda por Hora do Dia</h3>
      <div className={styles.peakBars} style={{ alignItems: "flex-end", height: 120, flexWrap: "wrap" }}>
        {(data.hourData ?? []).filter((_, i) => i >= 7 && i <= 21).map((h) => (
          <div key={h.hour} className={styles.peakBarItem}>
            <div className={styles.peakBarTrack}>
              <div
                className={styles.peakBarFill}
                style={{
                  height: `${(h.count / hourMax) * 100}%`,
                  background: h.isGap ? "#ef4444" : "#6e0ad4",
                }}
              />
            </div>
            <span className={styles.peakBarLabel}>{h.hour}</span>
            <span className={styles.peakBarCount}>{h.count}</span>
          </div>
        ))}
      </div>
      <p style={{ fontSize: "0.75rem", color: "#9ca3af", marginTop: "0.5rem" }}>
        <span style={{ color: "#ef4444" }}>■</span> Vermelho = horário com baixa demanda
      </p>

      <h3 className={styles.sectionTitle}>Demanda por Dia da Semana</h3>
      <div className={styles.peakBars} style={{ alignItems: "flex-end", height: 120 }}>
        {(data.dowData ?? []).map((d) => (
          <div key={d.day} className={styles.peakBarItem}>
            <div className={styles.peakBarTrack}>
              <div
                className={styles.peakBarFill}
                style={{
                  height: `${(d.count / dowMax) * 100}%`,
                  background: d.isGap ? "#ef4444" : "#10b981",
                }}
              />
            </div>
            <span className={styles.peakBarLabel}>{d.day}</span>
            <span className={styles.peakBarCount}>{d.count}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── Relatório de Insights IA ─────────────────────────────────────────────────

function InsightsReport({ data }) {
  return (
    <section>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
        <p style={{ color: "#6b7280", fontSize: "0.875rem", margin: 0 }}>
          Gerado em: {data.generatedAt ? new Date(data.generatedAt).toLocaleString("pt-BR") : "—"}
        </p>
        <span style={{ fontSize: "0.75rem", color: "#9ca3af", background: "#f3f4f6", padding: "2px 8px", borderRadius: 12 }}>
          Cache de 24h
        </span>
      </div>

      <div style={{ margin: "1.5rem 0", padding: "1.25rem", background: "linear-gradient(135deg, #6e0ad4 0%, #9333ea 100%)", borderRadius: 12, color: "#fff" }}>
        <div style={{ fontSize: "0.75rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", opacity: 0.85, marginBottom: "0.5rem" }}>
          🎯 Principal Oportunidade
        </div>
        <p style={{ margin: 0, fontSize: "1rem", lineHeight: 1.5 }}>{data.topOpportunity}</p>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
        {(data.insights ?? []).map((insight, i) => (
          <div
            key={i}
            style={{
              padding: "1rem 1.25rem",
              background: "#f9fafb",
              borderRadius: 8,
              borderLeft: "3px solid #6e0ad4",
              display: "flex",
              gap: "0.75rem",
              alignItems: "flex-start",
            }}
          >
            <span style={{ background: "#6e0ad4", color: "#fff", borderRadius: "50%", width: 24, height: 24, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.75rem", fontWeight: 700, flexShrink: 0 }}>
              {i + 1}
            </span>
            <p style={{ margin: 0, color: "#374151", fontSize: "0.9rem", lineHeight: 1.6 }}>{insight}</p>
          </div>
        ))}
      </div>

      {data.metrics && (
        <>
          <h3 className={styles.sectionTitle} style={{ marginTop: "2rem" }}>Métricas Utilizadas</h3>
          <div className={styles.cardsGrid}>
            <MetricCard label="Agendamentos" value={data.metrics.totalAppointments} color="#6e0ad4" />
            <MetricCard label="Cancelamentos" value={data.metrics.cancelledAppointments} color="#ef4444" sub={`${data.metrics.cancellationRate}% de cancelamento`} />
            <MetricCard label="Mensagens" value={data.metrics.totalMessages} color="#3b82f6" />
            <MetricCard label="Sessões Ativas" value={data.metrics.activeSessions} color="#10b981" />
            <MetricCard label="Total de Clientes" value={data.metrics.totalClients} color="#f59e0b" />
          </div>
        </>
      )}
    </section>
  );
}
